import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  FileBarChart2,
  UploadCloud,
  Wand2,
  ShieldCheck,
  Stethoscope,
  Plug,
  UserCircle2,
  LogOut,
  Leaf,
  Headphones,
  Phone,
  Megaphone,
  Zap,
  Tag,
  Trophy,
  Timer,
  Filter,
  ListChecks,
  ClipboardCheck,
  Repeat2,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { NAV, AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import FallingLeaves from "@/components/common/FallingLeaves";
import { useLaafficUnmapped, LaafficUnmappedBanner } from "@/components/laaffic/LaafficUnmappedBanner";

const financeNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: NAV.dashboard, end: true },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: NAV.reports },
];

const opsNav = [
  { to: "/ops", label: "Ops dashboard", icon: LayoutDashboard, testId: "nav-ops-dashboard", end: true },
  { to: "/ops/lite-report", label: "Lite report", icon: Zap, testId: "nav-ops-lite-report" },
  { to: "/ops/scorecard", label: "Agent scorecard", icon: Trophy, testId: "nav-ops-scorecard" },
  { to: "/ops/time-to-convert", label: "Time to convert", icon: Timer, testId: "nav-ops-time-to-convert" },
  { to: "/ops/lead-funnel", label: "Lead funnel", icon: Filter, testId: "nav-ops-lead-funnel" },
  { to: "/ops/disposition", label: "Disposition deep-dive", icon: ListChecks, testId: "nav-ops-disposition" },
  { to: "/ops/data-quality", label: "Data quality", icon: ClipboardCheck, testId: "nav-ops-data-quality" },
  { to: "/ops/retention", label: "Retention · repeat", icon: Repeat2, testId: "nav-ops-retention" },
  { to: "/ops/peak-hours", label: "Peak call hours", icon: CalendarClock, testId: "nav-ops-peak-hours" },
  { to: "/ops/uploads", label: "Ops uploads", icon: Phone, testId: "nav-ops-uploads" },
  { to: "/ops/upload", label: "New upload", icon: UploadCloud, testId: "nav-ops-upload", uploadOnly: true },
  { to: "/ops/leads", label: "Generate leads", icon: Tag, testId: "nav-ops-leads" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, testId: "nav-campaigns" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isOpsOnly = user?.role === "ops" && !user?.is_admin && !user?.is_super_admin;
  const showFinance = !isOpsOnly;
  const showOps = user?.role === "ops" || user?.is_admin || user?.is_super_admin;
  const canUpload = !!(user?.is_admin || user?.is_super_admin || user?.can_upload_ops);
  const visibleOpsNav = opsNav.filter((n) => !n.uploadOnly || canUpload);
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const laafficSummary = useLaafficUnmapped(isAdmin);
  const { pathname } = useLocation();
  const laafficCount = (laafficSummary?.total || 0);

  return (
    <div className="relative min-h-screen">
      <FallingLeaves />
      <div className="relative z-10 flex min-h-screen">
        {/* Sidebar */}
        <aside className="glass-sidebar hidden md:flex md:w-64 flex-col p-5">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#0A84FF,#5B8C5A)" }}>
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-outfit font-semibold tracking-tight text-slate-900">Outstanding Dime</div>
              <div className="text-xs text-slate-500">Reactivation analytics</div>
            </div>
          </div>

          <nav className="flex-1 space-y-1">
            {showFinance && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 mb-1">Finance</div>
                {financeNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                        isActive
                          ? "bg-white/80 text-slate-900 shadow-sm"
                          : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                      }`
                    }
                  >
                    <n.icon className="w-4 h-4" />
                    {n.label}
                  </NavLink>
                ))}
              </>
            )}
            {showOps && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 mb-1 mt-4 flex items-center gap-1">
                  <Headphones className="w-3 h-3" /> Ops
                </div>
                {visibleOpsNav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testId}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                        isActive
                          ? "bg-white/80 text-slate-900 shadow-sm"
                          : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                      }`
                    }
                  >
                    <n.icon className="w-4 h-4" />
                    {n.label}
                  </NavLink>
                ))}
              </>
            )}
            {(user?.is_admin || user?.is_super_admin) && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 mb-1 mt-4">Admin</div>
                <NavLink
                  to="/admin"
                  data-testid={NAV.admin}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                      isActive
                        ? "bg-white/80 text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`
                  }
                >
                  <ShieldCheck className="w-4 h-4" />
                  Admin
                </NavLink>
                <NavLink
                  to="/admin/diagnose"
                  data-testid="nav-diagnose"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                      isActive
                        ? "bg-white/80 text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`
                  }
                >
                  <Stethoscope className="w-4 h-4" />
                  Diagnose
                </NavLink>
                <NavLink
                  to="/admin/integrations"
                  data-testid="nav-integrations"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                      isActive
                        ? "bg-white/80 text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`
                  }
                >
                  <Plug className="w-4 h-4" />
                  Integrations
                </NavLink>
                <NavLink
                  to="/admin/laaffic"
                  data-testid="nav-laaffic"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                      isActive
                        ? "bg-white/80 text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`
                  }
                >
                  <Plug className="w-4 h-4" />
                  Laaffic integration
                  {laafficCount > 0 && (
                    <span className="ml-auto min-w-[20px] px-1.5 h-5 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center tabular-nums" data-testid="nav-laaffic-badge">
                      {laafficCount > 999 ? "999+" : laafficCount}
                    </span>
                  )}
                </NavLink>
              </>
            )}
            {/* Ops-only users also get the Diagnose link (scoped to their
                assigned campaigns on the backend). */}
            {user?.role === "ops" && !user?.is_admin && !user?.is_super_admin && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 px-3 mb-1 mt-4">Tools</div>
                <NavLink
                  to="/admin/diagnose"
                  data-testid="nav-diagnose-ops"
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-xl text-sm btn-tactile transition-colors ${
                      isActive
                        ? "bg-white/80 text-slate-900 shadow-sm"
                        : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                    }`
                  }
                >
                  <Stethoscope className="w-4 h-4" />
                  Diagnose
                </NavLink>
              </>
            )}
          </nav>

          <div className="mt-auto pt-4 border-t border-white/60 text-[11px] text-slate-500 px-1">
            Signed in — use the top-right menu to log out.
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 p-4 md:p-8 max-w-[1500px] mx-auto w-full">
          {/* Top bar with user chip + logout */}
          <div className="flex items-center justify-end gap-3 mb-6">
            <button
              data-testid={NAV.profile}
              onClick={() => navigate("/profile")}
              className="glass-card px-3 py-1.5 flex items-center gap-2 btn-tactile hover:bg-white/70"
            >
              <UserCircle2 className="w-4 h-4 text-slate-600" />
              <div className="text-left leading-tight">
                <div className="text-xs font-medium text-slate-900 truncate max-w-[160px]">{user?.email}</div>
                <div className="text-[10px] text-slate-500">
                  {user?.is_super_admin ? "Super Admin" : user?.is_admin ? "Admin" : "Analyst"}
                </div>
              </div>
            </button>
            <Button
              variant="outline"
              size="sm"
              className="btn-tactile gap-2 bg-white/70"
              data-testid={AUTH.logoutBtn}
              onClick={logout}
            >
              <LogOut className="w-4 h-4" /> Log out
            </Button>
          </div>
          {isAdmin && (pathname === "/" || pathname === "/ops") && <LaafficUnmappedBanner summary={laafficSummary} />}
          {children}
        </main>
      </div>
    </div>
  );
}
